import React, { useState, useEffect } from 'react';
import { X, Briefcase, Palette, Info, Check } from 'lucide-react';

/**
 * Job Title Form Component
 * 
 * Used for creating and editing job titles.
 * 
 * @param {Object} props
 * @param {Object} props.initialData - Job title data for editing (null for new)
 * @param {Array} props.colors - Array of color options { value, label }
 * @param {Function} props.onSubmit - Called with form data on submit
 * @param {Function} props.onCancel - Called when cancel is clicked
 */
const JobTitleForm = ({ initialData, colors, onSubmit, onCancel }) => {
  const isEditing = !!initialData;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    isActive: true,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with data when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        color: initialData.color || '#3B82F6',
        isActive: initialData.isActive !== false,
      });
    }
  }, [initialData]);

  // Validate form
  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Job title name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }

    if (formData.color && !/^#[0-9A-Fa-f]{6}$/.test(formData.color)) {
      newErrors.color = 'Color must be a valid hex code (e.g., #3B82F6)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is modified
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        color: formData.color,
        isActive: formData.isActive,
      });
    } catch (err) {
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle color selection
  const handleColorSelect = (colorValue) => {
    handleChange('color', colorValue);
  };

  // Custom color input handler
  const handleCustomColorChange = (e) => {
    let value = e.target.value;
    // Add # if not present
    if (value && !value.startsWith('#')) {
      value = '#' + value;
    }
    handleChange('color', value);
  };

  return (
    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-100">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Briefcase className="text-purple-600" size={20} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {isEditing ? 'Edit Job Title' : 'Add Job Title'}
            </h3>
            <p className="text-sm text-gray-500">
              {isEditing ? 'Update job title details' : 'Create a new job title for employees'}
            </p>
          </div>
        </div>
        <button 
          onClick={onCancel} 
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          disabled={isSubmitting}
        >
          <X size={20} />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Name Field */}
        <div>
          <label className="form-label">
            Job Title Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={`form-input ${errors.name ? 'border-red-300 focus:ring-red-500' : ''}`}
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g., Lead Teacher"
            disabled={isSubmitting}
            autoFocus
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <Info size={14} className="mr-1" />
              {errors.name}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            This is how the title will appear when assigning to employees
          </p>
        </div>

        {/* Description Field */}
        <div>
          <label className="form-label">Description</label>
          <textarea
            className={`form-input ${errors.description ? 'border-red-300 focus:ring-red-500' : ''}`}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Optional description of this role"
            rows={3}
            disabled={isSubmitting}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <Info size={14} className="mr-1" />
              {errors.description}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {formData.description.length}/500 characters
          </p>
        </div>

        {/* Color Field */}
        <div>
          <label className="form-label flex items-center space-x-2">
            <Palette size={16} />
            <span>Color</span>
          </label>
          
          {/* Color Presets */}
          <div className="grid grid-cols-6 gap-2 mb-3">
            {colors.map((color) => (
              <button
                key={color.value}
                type="button"
                onClick={() => handleColorSelect(color.value)}
                className={`w-10 h-10 rounded-lg border-2 transition-all ${
                  formData.color === color.value 
                    ? 'border-gray-900 scale-110 shadow-md' 
                    : 'border-transparent hover:scale-105'
                }`}
                style={{ backgroundColor: color.value }}
                title={color.label}
                disabled={isSubmitting}
              >
                {formData.color === color.value && (
                  <Check size={16} className="text-white mx-auto" />
                )}
              </button>
            ))}
          </div>

          {/* Custom Color Input */}
          <div className="flex items-center space-x-2">
            <div 
              className="w-8 h-8 rounded border border-gray-200"
              style={{ backgroundColor: formData.color }}
            />
            <input
              type="text"
              className={`form-input flex-1 ${errors.color ? 'border-red-300 focus:ring-red-500' : ''}`}
              value={formData.color}
              onChange={handleCustomColorChange}
              placeholder="#3B82F6"
              disabled={isSubmitting}
            />
          </div>
          {errors.color && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <Info size={14} className="mr-1" />
              {errors.color}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Choose a color for visual distinction in the employee list
          </p>
        </div>

        {/* Active Status */}
        {isEditing && (
          <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => handleChange('isActive', e.target.checked)}
              className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 mt-0.5"
              disabled={isSubmitting}
            />
            <div>
              <label htmlFor="isActive" className="font-medium text-gray-700 cursor-pointer">
                Active
              </label>
              <p className="text-sm text-gray-500">
                Inactive job titles won't appear in dropdowns when creating new employees
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center space-x-2 disabled:opacity-50"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <span>{isEditing ? 'Save Changes' : 'Add Job Title'}</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default JobTitleForm;
